import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import throttle from "lodash/throttle";
import { Loader2, MapPin, Search } from "lucide-react";
import { cn } from "lib/utils";

export interface MapSearchSelection {
  lat: number;
  lng: number;
  address: string;
}

interface MapSearchProps {
  /** Restricts predictions to the city (prefixed into the query, legacy parity). */
  city: { name: string; nameWithState?: string | null } | null;
  /** ISO country code restricting predictions (legacy componentRestrictions). */
  countryCode: string;
  onSelect: (selection: MapSearchSelection) => void;
  placeholder?: string;
  cypressId?: string;
}

type Prediction = google.maps.places.AutocompletePrediction;

// The in-map location search (AC-S1): Google Places autocomplete restricted to
// the selected city + account country, styled to match the builder (shadcn) and
// the Figma overlay rather than the legacy Material-UI field. Mirrors the legacy
// mechanics — throttled getPlacePredictions, city-prefixed input, country
// restriction, getDetails → geometry — but stays free of the MUI patchwork the
// new builder is replacing. Channel-specific behaviour on select lives in the
// caller (H2H creates a pin, D2D pans/zooms).
export const MapSearch = ({
  city,
  countryCode,
  onSelect,
  placeholder = "Search for a specific location or create new ones",
  cypressId = "targeting-map-search",
}: MapSearchProps) => {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  // Services are created lazily once the Maps script is ready (guaranteed inside
  // MapFrame). PlacesService needs a DOM node but never renders it.
  useEffect(() => {
    const g = window.google?.maps;
    if (!g) return;
    if (!autocompleteService.current) autocompleteService.current = new g.places.AutocompleteService();
    if (!placesService.current) placesService.current = new g.places.PlacesService(document.createElement("div"));
  }, []);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const fetchPredictions = useMemo(
    () =>
      throttle((request: google.maps.places.AutocompletionRequest) => {
        autocompleteService.current?.getPlacePredictions(request, (results) => {
          setOptions(results ?? []);
          setLoading(false);
        });
      }, 200),
    []
  );

  const handleInput = useCallback(
    (value: string) => {
      setInput(value);
      if (!value.trim() || !autocompleteService.current) {
        setOptions([]);
        setOpen(false);
        return;
      }
      setLoading(true);
      setOpen(true);
      fetchPredictions({
        // Prefix the city so predictions stay local to it, matching the legacy field.
        input: city ? `${city.nameWithState || city.name}, ${value}` : value,
        language: "en",
        componentRestrictions: countryCode ? { country: countryCode } : undefined,
      });
    },
    [city, countryCode, fetchPredictions]
  );

  const pick = useCallback(
    (prediction: Prediction) => {
      const service = placesService.current;
      if (!service) return;
      service.getDetails(
        { placeId: prediction.place_id, fields: ["geometry", "formatted_address"] },
        (details, status) => {
          const location = details?.geometry?.location;
          // Only commit + clear on a successful lookup. On failure leave the input
          // and suggestions in place so the user can retry, rather than silently
          // clearing the field with nothing selected.
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !location) return;
          onSelect({
            lat: location.lat(),
            lng: location.lng(),
            address: details?.formatted_address || prediction.description,
          });
          setInput("");
          setOptions([]);
          setOpen(false);
        }
      );
    },
    [onSelect]
  );

  return (
    <div className="relative w-full">
      <div className="flex h-11 items-center gap-2 rounded-lg border border-solid border-border bg-background px-3 shadow-sm">
        <Search className="size-4 shrink-0 text-muted-foreground" />
        <input
          type="text"
          value={input}
          placeholder={placeholder}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => options.length > 0 && setOpen(true)}
          // Delay close so a click on an option (which blurs the input) still lands.
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 150);
          }}
          className="h-full w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          data-cypress-id={cypressId}
        />
        {loading && <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />}
      </div>
      {open && options.length > 0 && (
        <ul
          className="absolute inset-x-0 top-[calc(100%+4px)] z-20 max-h-72 overflow-auto rounded-lg border border-solid border-border bg-popover py-1 shadow-lg"
          data-cypress-id={`${cypressId}-options`}
        >
          {options.map((option) => (
            <li key={option.place_id}>
              <button
                type="button"
                // mouseDown (not click) fires before the input blur closes the list.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(option);
                }}
                className={cn(
                  "flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-popover-foreground transition-colors hover:bg-accent"
                )}
                data-cypress-id={`${cypressId}-option-${option.place_id}`}
              >
                <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="font-medium">{option.structured_formatting?.main_text || option.description}</span>
                  {option.structured_formatting?.secondary_text && (
                    <span className="text-muted-foreground"> {option.structured_formatting.secondary_text}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
